import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';

interface UsageCellProps {
  usage: number | null | undefined;
  limit: number | null | undefined;
  label?: string;
}

function formatLimit(limit: number | null): string {
  if (limit == null) {
    return '∞';
  }
  return limit.toLocaleString();
}

export function UsageCell({ usage, limit, label = 'creatives' }: UsageCellProps) {
  const safeUsage = typeof usage === 'number' && Number.isFinite(usage) ? usage : 0;
  const safeLimit = typeof limit === 'number' && Number.isFinite(limit) ? limit : null;
  const isUnlimited = safeLimit == null;
  const ratio = !isUnlimited && safeLimit > 0 ? Math.min(safeUsage / safeLimit, 1) : 0;
  const percentage = isUnlimited ? 0 : Math.round(ratio * 100);
  const isNearLimit = !isUnlimited && percentage >= 85;
  const limitLabel = formatLimit(safeLimit);

  return (
    <div className="flex min-w-[140px] flex-col gap-1.5">
      <span className={cn(portalDs.tableCellMono, 'text-[13px] text-[#0D0F1A]')}>
        {safeUsage.toLocaleString()}
        {' / '}
        {limitLabel}
        {' '}
        {label}
      </span>
      {isUnlimited ? (
        <span className={cn(portalDs.caption, 'text-[#9CA3AF]')}>
          Unlimited
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#EDEEF2]"
            role="progressbar"
            aria-valuenow={safeUsage}
            aria-valuemin={0}
            aria-valuemax={safeLimit ?? undefined}
            aria-label={`${safeUsage} of ${limitLabel} ${label} used`}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all',
                isNearLimit ? 'bg-[#DC2626]' : 'bg-[#2563EB]'
              )}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className={cn(portalDs.caption, 'whitespace-nowrap')}>
            {percentage}
            %
          </span>
        </div>
      )}
    </div>
  );
}
